import {
  Add,
  Delete,
} from '@mui/icons-material'
import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import type { ContactStage, MarketingEmailStatus } from '@otw/models/leads/contacts'
import {
  AUDIENCE_FILTER_FIELD_LABEL,
  AUDIENCE_FILTER_FIELDS,
  AUDIENCE_FILTER_OPERATOR_LABEL,
  AUDIENCE_UNASSIGNED_OWNER,
  US_STATE_ABBREVIATIONS,
  defaultAudienceRule,
  newAudienceNodeId,
  operatorsForAudienceField,
  type AudienceFilterField,
  type AudienceFilterGroup,
  type AudienceFilterNode,
  type AudienceFilterOperator,
  type AudienceFilterRule,
} from '@otw/models/leads/marketingCampaign'
import type { Terminal } from '@otw/models/leads/terminals'
import { getTerminalDisplayName } from '@otw/models/leads/terminals'
import type { PrivateUserInfoType } from '@otw/models'
import React from 'react'
import { getUserDisplayName } from 'hooks/useUsers'

const STAGE_OPTIONS: { value: ContactStage; label: string }[] = [
  { value: 'prospect', label: 'Prospect' },
  { value: 'opportunity', label: 'Opportunity' },
  { value: 'customer', label: 'Customer' },
  { value: 'unqualified', label: 'Unqualified' },
]

const MARKETING_STATUS_OPTIONS: { value: MarketingEmailStatus; label: string }[] = [
  { value: 'subscribed', label: 'Subscribed' },
  { value: 'unsubscribed', label: 'Unsubscribed' },
]

export interface AudienceFilterBuilderProps {
  value: AudienceFilterGroup
  onChange: (next: AudienceFilterGroup) => void
  salesOwners: PrivateUserInfoType[]
  inventoryOwners: PrivateUserInfoType[]
  terminals: Terminal[]
  disabled?: boolean
}

const scalarValue = (rule: AudienceFilterRule): string =>
  Array.isArray(rule.value) ? String(rule.value[0] ?? '') : String(rule.value ?? '')

const replaceChild = (
  group: AudienceFilterGroup,
  childId: string,
  next: AudienceFilterNode,
): AudienceFilterGroup => ({
  ...group,
  children: group.children.map((child) => (child.id === childId ? next : child)),
})

const removeChild = (group: AudienceFilterGroup, childId: string): AudienceFilterGroup => ({
  ...group,
  children: group.children.filter((child) => child.id !== childId),
})

const AudienceFilterBuilder: React.FC<AudienceFilterBuilderProps> = ({
  value,
  onChange,
  salesOwners,
  inventoryOwners,
  terminals,
  disabled,
}) => (
  <AudienceGroupEditor
    group={value}
    onChange={onChange}
    salesOwners={salesOwners}
    inventoryOwners={inventoryOwners}
    terminals={terminals}
    disabled={disabled}
    depth={0}
  />
)

interface GroupEditorProps {
  group: AudienceFilterGroup
  onChange: (next: AudienceFilterGroup) => void
  salesOwners: PrivateUserInfoType[]
  inventoryOwners: PrivateUserInfoType[]
  terminals: Terminal[]
  disabled?: boolean
  depth: number
}

const AudienceGroupEditor: React.FC<GroupEditorProps> = ({
  group,
  onChange,
  salesOwners,
  inventoryOwners,
  terminals,
  disabled,
  depth,
}) => {
  const addRule = () => {
    onChange({
      ...group,
      children: [...group.children, defaultAudienceRule()],
    })
  }

  const addGroup = () => {
    onChange({
      ...group,
      children: [
        ...group.children,
        {
          kind: 'group',
          id: newAudienceNodeId(),
          combinator: group.combinator === 'and' ? 'or' : 'and',
          children: [defaultAudienceRule()],
        },
      ],
    })
  }

  return (
    <Paper
      variant="outlined"
      sx={{ p: 1.5, bgcolor: depth === 0 ? 'transparent' : 'action.hover' }}
    >
      <Stack spacing={1.25}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          alignItems={{ sm: 'center' }}
          justifyContent="space-between"
        >
          <FormControl size="small" sx={{ minWidth: 140 }} disabled={disabled}>
            <InputLabel>Match</InputLabel>
            <Select
              label="Match"
              value={group.combinator}
              onChange={(event) =>
                onChange({
                  ...group,
                  combinator: event.target.value === 'or' ? 'or' : 'and',
                })
              }
            >
              <MenuItem value="and">All (AND)</MenuItem>
              <MenuItem value="or">Any (OR)</MenuItem>
            </Select>
          </FormControl>
          <Stack direction="row" spacing={1}>
            <Button size="small" startIcon={<Add />} onClick={addRule} disabled={disabled}>
              Add rule
            </Button>
            <Button size="small" startIcon={<Add />} onClick={addGroup} disabled={disabled}>
              Add group
            </Button>
          </Stack>
        </Stack>
        {group.children.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No rules — this group matches all contacts.
          </Typography>
        ) : (
          group.children.map((child) =>
            child.kind === 'group' ? (
              <Stack key={child.id} direction="row" spacing={0.5} alignItems="flex-start">
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <AudienceGroupEditor
                    group={child}
                    onChange={(next) => onChange(replaceChild(group, child.id, next))}
                    salesOwners={salesOwners}
                    inventoryOwners={inventoryOwners}
                    terminals={terminals}
                    disabled={disabled}
                    depth={depth + 1}
                  />
                </Box>
                <IconButton
                  aria-label="Remove group"
                  size="small"
                  onClick={() => onChange(removeChild(group, child.id))}
                  disabled={disabled}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </Stack>
            ) : (
              <AudienceRuleEditor
                key={child.id}
                rule={child}
                onChange={(next) => onChange(replaceChild(group, child.id, next))}
                onRemove={() => onChange(removeChild(group, child.id))}
                salesOwners={salesOwners}
                inventoryOwners={inventoryOwners}
                terminals={terminals}
                disabled={disabled}
              />
            ),
          )
        )}
      </Stack>
    </Paper>
  )
}

interface RuleEditorProps {
  rule: AudienceFilterRule
  onChange: (next: AudienceFilterRule) => void
  onRemove: () => void
  salesOwners: PrivateUserInfoType[]
  inventoryOwners: PrivateUserInfoType[]
  terminals: Terminal[]
  disabled?: boolean
}

const AudienceRuleEditor: React.FC<RuleEditorProps> = ({
  rule,
  onChange,
  onRemove,
  salesOwners,
  inventoryOwners,
  terminals,
  disabled,
}) => {
  const operators = operatorsForAudienceField(rule.field)
  const operator = operators.includes(rule.operator) ? rule.operator : operators[0]

  const handleFieldChange = (field: AudienceFilterField) => {
    onChange(defaultAudienceRule(field))
  }

  const handleOperatorChange = (nextOperator: AudienceFilterOperator) => {
    let value = rule.value
    if (nextOperator === 'olderThanDays' && (typeof value !== 'number' || !Number.isFinite(value))) {
      value = 90
    }
    if (nextOperator === 'before' || nextOperator === 'after') {
      value = typeof value === 'string' && value.includes('-') ? value : ''
    }
    onChange({ ...rule, operator: nextOperator, value })
  }

  return (
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      spacing={1}
      alignItems={{ md: 'center' }}
    >
      <FormControl size="small" sx={{ minWidth: 180, flex: 1 }} disabled={disabled}>
        <InputLabel>Field</InputLabel>
        <Select
          label="Field"
          value={rule.field}
          onChange={(event) => handleFieldChange(event.target.value as AudienceFilterField)}
        >
          {AUDIENCE_FILTER_FIELDS.map((field) => (
            <MenuItem key={field} value={field}>
              {AUDIENCE_FILTER_FIELD_LABEL[field]}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl size="small" sx={{ minWidth: 150 }} disabled={disabled}>
        <InputLabel>Operator</InputLabel>
        <Select
          label="Operator"
          value={operator}
          onChange={(event) =>
            handleOperatorChange(event.target.value as AudienceFilterOperator)
          }
        >
          {operators.map((op) => (
            <MenuItem key={op} value={op}>
              {AUDIENCE_FILTER_OPERATOR_LABEL[op]}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Box sx={{ flex: 1.2, minWidth: 160 }}>
        <RuleValueInput
          rule={{ ...rule, operator }}
          onChange={onChange}
          salesOwners={salesOwners}
          inventoryOwners={inventoryOwners}
          terminals={terminals}
          disabled={disabled}
        />
      </Box>
      <IconButton aria-label="Remove rule" size="small" onClick={onRemove} disabled={disabled}>
        <Delete fontSize="small" />
      </IconButton>
    </Stack>
  )
}

interface RuleValueProps {
  rule: AudienceFilterRule
  onChange: (next: AudienceFilterRule) => void
  salesOwners: PrivateUserInfoType[]
  inventoryOwners: PrivateUserInfoType[]
  terminals: Terminal[]
  disabled?: boolean
}

const RuleValueInput: React.FC<RuleValueProps> = ({
  rule,
  onChange,
  salesOwners,
  inventoryOwners,
  terminals,
  disabled,
}) => {
  const value = scalarValue(rule)

  if (rule.field === 'lastActivityAt' && rule.operator === 'olderThanDays') {
    return (
      <TextField
        size="small"
        type="number"
        label="Days"
        fullWidth
        value={typeof rule.value === 'number' ? rule.value : Number(value) || ''}
        onChange={(event) =>
          onChange({ ...rule, value: Number(event.target.value) || 0 })
        }
        disabled={disabled}
        inputProps={{ min: 0, step: 1 }}
      />
    )
  }

  if (rule.field === 'lastActivityAt') {
    return (
      <TextField
        size="small"
        type="date"
        label="Date"
        fullWidth
        InputLabelProps={{ shrink: true }}
        value={value}
        onChange={(event) => onChange({ ...rule, value: event.target.value })}
        disabled={disabled}
      />
    )
  }

  if (rule.field === 'stage' || rule.field === 'inventoryStage') {
    return (
      <FormControl size="small" fullWidth disabled={disabled}>
        <InputLabel>Stage</InputLabel>
        <Select
          label="Stage"
          value={value}
          onChange={(event) => onChange({ ...rule, value: event.target.value })}
        >
          {STAGE_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    )
  }

  if (rule.field === 'marketingEmailStatus') {
    return (
      <FormControl size="small" fullWidth disabled={disabled}>
        <InputLabel>Status</InputLabel>
        <Select
          label="Status"
          value={value || 'subscribed'}
          onChange={(event) => onChange({ ...rule, value: event.target.value })}
        >
          {MARKETING_STATUS_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    )
  }

  if (rule.field === 'salesOwner' || rule.field === 'inventoryOwner') {
    const owners = rule.field === 'salesOwner' ? salesOwners : inventoryOwners
    return (
      <FormControl size="small" fullWidth disabled={disabled}>
        <InputLabel>Owner</InputLabel>
        <Select
          label="Owner"
          value={value}
          onChange={(event) => onChange({ ...rule, value: event.target.value })}
        >
          <MenuItem value={AUDIENCE_UNASSIGNED_OWNER}>Unassigned</MenuItem>
          {owners.map((user) => (
            <MenuItem key={String(user.id)} value={String(user.id)}>
              {getUserDisplayName(user)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    )
  }

  if (rule.field === 'state') {
    return (
      <FormControl size="small" fullWidth disabled={disabled}>
        <InputLabel>State</InputLabel>
        <Select
          label="State"
          value={value}
          onChange={(event) => onChange({ ...rule, value: event.target.value })}
        >
          {US_STATE_ABBREVIATIONS.map((abbr) => (
            <MenuItem key={abbr} value={abbr}>
              {abbr}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    )
  }

  if (rule.field === 'terminal') {
    return (
      <FormControl size="small" fullWidth disabled={disabled}>
        <InputLabel>Terminal</InputLabel>
        <Select
          label="Terminal"
          value={value}
          onChange={(event) => onChange({ ...rule, value: event.target.value })}
        >
          {terminals.map((terminal) => (
            <MenuItem key={terminal.id} value={terminal.id}>
              {getTerminalDisplayName(terminal)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    )
  }

  return (
    <TextField
      size="small"
      label="Value"
      fullWidth
      value={value}
      onChange={(event) => onChange({ ...rule, value: event.target.value })}
      disabled={disabled}
    />
  )
}

export default AudienceFilterBuilder
